import { ApiError } from '@lib/api';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import {
    getFailedItems,
    getReleaseOperation,
    previewClientsOperation,
    retryOperation,
    startAllOperation,
    startClientsOperation,
} from '../api/release-components';
import { createTerminalInvalidation } from '../components/operation-invalidation';
import { nextFailedCursor } from '../components/operation-results';
import { pollInterval } from '../components/operation-status';
import { runChunkedPreview, type PreviewProgress } from '../components/preview-chunks';

import { releaseKeys } from './use-release-components';

import type { LastAction } from '../schemas/release-component';

export const operationKeys = {
    detail: (releaseId: string, operationId: string) =>
        [...releaseKeys.all, 'operation', releaseId, operationId] as const,
    failed: (releaseId: string, operationId: string) =>
        [...releaseKeys.all, 'operation-failed', releaseId, operationId] as const,
};

function isMissing(error: unknown): boolean {
    return error instanceof ApiError && error.status === 404;
}

/**
 * The polled operation (design B3): every 3 s while running, every 30 s while
 * stalled, never once terminal or gone. A 404 is an answer, not a blip, so it is
 * not retried and ends polling.
 */
export function useReleaseOperation(releaseId: string, operationId: string | undefined) {
    return useQuery({
        queryKey: operationKeys.detail(releaseId, operationId ?? ''),
        queryFn: ({ signal }) => getReleaseOperation(releaseId, operationId ?? '', signal),
        enabled: operationId !== undefined,
        retry: (failureCount, error) => !isMissing(error) && failureCount < 1,
        refetchInterval: (query) =>
            pollInterval(
                query.state.data,
                query.state.error === null
                    ? 'none'
                    : isMissing(query.state.error)
                      ? 'missing'
                      : 'error',
            ),
        networkMode: 'always',
    });
}

/**
 * Invalidates the release detail, flag keys, tenant flags and holdings once when
 * the operation moves to a terminal state. `seed` is the detail's own last action,
 * so a first poll that already reads terminal still counts as a transition.
 */
export function useOperationInvalidation(
    releaseId: string,
    operationId: string | undefined,
    state: LastAction['state'] | undefined,
    seed: LastAction | undefined,
): void {
    const queryClient = useQueryClient();
    const observe = useMemo(
        () =>
            createTerminalInvalidation(releaseId, (queryKey) => {
                void queryClient.invalidateQueries({ queryKey });
            }),
        [releaseId, queryClient],
    );

    useEffect(() => {
        if (seed !== undefined) observe(seed.operationId, seed.state);
    }, [observe, seed]);

    useEffect(() => {
        if (operationId !== undefined) observe(operationId, state);
    }, [observe, operationId, state]);
}

/**
 * Start an all-clients operation. The detail is refetched on success and on any
 * failure after the lock was taken (a 503 leaves a stalled operation behind), so
 * the page shows what the server holds. Never retried.
 */
export function useStartAllOperation(releaseId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: { readonly enabled: boolean; readonly reason?: string }) =>
            startAllOperation(releaseId, input),
        onSettled: () => queryClient.invalidateQueries({ queryKey: releaseKeys.detail(releaseId) }),
        retry: false,
        networkMode: 'always',
    });
}

/** Start an operation for specific clients. Same refetch and no-retry policy as all clients. */
export function useStartClientsOperation(releaseId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: {
            readonly enabled: boolean;
            readonly tenantIds: readonly string[];
            readonly reason?: string;
        }) => startClientsOperation(releaseId, input),
        onSettled: () => queryClient.invalidateQueries({ queryKey: releaseKeys.detail(releaseId) }),
        retry: false,
        networkMode: 'always',
    });
}

/**
 * The turn-off overwrite preview over any number of clients, in sequential chunks
 * of 100 with progress. Used by the turn-off dialog for specific clients.
 */
export function useClientsPreview(releaseId: string) {
    const [progress, setProgress] = useState<PreviewProgress | null>(null);
    const mutation = useMutation({
        mutationFn: (tenantIds: readonly string[]) =>
            runChunkedPreview(
                tenantIds,
                (chunk) => previewClientsOperation(releaseId, { tenantIds: chunk }),
                setProgress,
            ),
        retry: false,
        networkMode: 'always',
    });
    return { ...mutation, progress };
}

/**
 * Retry the latest operation (pending and failed items only). `retry: false`: a
 * second POST of a retry is the operator's decision, never the client's. Settling
 * refetches the release detail (its `lastAction` goes back to `running`) and the
 * operation, so polling restarts and `useOperationInvalidation` re-arms on the
 * `running` it then reads. A 503 leaves a stalled operation behind, so the refetch
 * runs on failure too.
 */
export function useRetryOperation(releaseId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (input: { readonly operationId: string; readonly scope: 'all' | 'clients' }) =>
            retryOperation(releaseId, input.operationId, input.scope),
        onSettled: (_data, _error, input) =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: releaseKeys.detail(releaseId) }),
                queryClient.invalidateQueries({
                    queryKey: operationKeys.detail(releaseId, input.operationId),
                }),
                queryClient.invalidateQueries({
                    queryKey: operationKeys.failed(releaseId, input.operationId),
                }),
            ]),
        retry: false,
        networkMode: 'always',
    });
}

/** Failed items of one operation, 50 per page, loaded on demand through the opaque cursor. */
export function useFailedItems(
    releaseId: string,
    operationId: string | undefined,
    enabled: boolean,
) {
    return useInfiniteQuery({
        queryKey: operationKeys.failed(releaseId, operationId ?? ''),
        queryFn: ({ signal, pageParam }) =>
            getFailedItems(releaseId, operationId ?? '', pageParam, signal),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: nextFailedCursor,
        enabled: enabled && operationId !== undefined,
        retry: false,
        networkMode: 'always',
    });
}
